import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Phone, MessageCircle, FileText, Heart, Shield, CheckCircle } from 'lucide-react';
import { breedingConditions, faqData, contactInfo, pricing } from '../data/mock';
import { Link } from 'react-router-dom';

const Conditions = () => {
  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-black to-black/95">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Conditions de <span className="text-[#cbb26a]">saillie</span>
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Toutes les informations pour une saillie réussie et sécurisée
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          {/* Modalites */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              Modalités de la <span className="text-[#cbb26a]">saillie</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {breedingConditions.map((section, index) => (
                <Card key={index} className="bg-white/5 border-[#cbb26a]/30 hover:bg-white/10 transition-all duration-300">
                  <CardHeader>
                    <CardTitle className="text-[#cbb26a] flex items-center space-x-2">
                      {index === 0 && <FileText size={20} />}
                      {index === 1 && <Heart size={20} />}
                      {index === 2 && <CheckCircle size={20} />}
                      {index === 3 && <Shield size={20} />}
                      <span>{section.title}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {section.items.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-white/80 text-sm">
                          <span className="text-[#cbb26a] mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Tarifs et paiement */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              Tarifs et <span className="text-[#cbb26a]">paiement</span>
            </h2>
            <div className="max-w-4xl mx-auto">
              <Card className="bg-white/5 border-[#cbb26a]/30 mb-6">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <div className="text-5xl font-bold text-[#cbb26a] mb-2">{pricing.studFee}€</div>
                    <div className="text-white/70">Tarif de la saillie</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-[#cbb26a] font-semibold mb-3">Prestations incluses</h3>
                      <ul className="space-y-2">
                        {pricing.included.map((item, index) => (
                          <li key={index} className="flex items-start space-x-2 text-white/80 text-sm">
                            <CheckCircle size={16} className="text-[#cbb26a] mt-1 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-[#cbb26a] font-semibold mb-3">Modalités de paiement</h3>
                      <ul className="space-y-2">
                        {pricing.payment.map((item, index) => (
                          <li key={index} className="flex items-start space-x-2 text-white/80 text-sm">
                            <CheckCircle size={16} className="text-[#cbb26a] mt-1 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <div className="text-center">
                <Button
                  asChild
                  size="lg"
                  className="bg-[#cbb26a] text-black hover:bg-[#d4c485] transition-all duration-300"
                >
                  <Link to="/tarifs">Voir tous les tarifs</Link>
                </Button>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              Questions <span className="text-[#cbb26a]">fréquentes</span>
            </h2>
            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="space-y-4">
                {faqData.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="bg-white/5 border border-[#cbb26a]/30 rounded-lg px-6"
                  >
                    <AccordionTrigger className="text-white hover:text-[#cbb26a] transition-colors duration-300">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-white/80">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-gradient-to-r from-[#cbb26a]/10 to-transparent rounded-xl p-12">
            <h3 className="text-3xl font-bold text-white mb-4">
              Des questions sur les conditions ?
            </h3>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              N'hésitez pas à nous contacter pour toute question ou demande d'information complémentaire
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-[#cbb26a] text-black hover:bg-[#d4c485] transition-all duration-300"
              >
                <a href={`https://wa.me/33${contactInfo.whatsapp.substring(1)}`} className="flex items-center space-x-2">
                  <MessageCircle size={20} />
                  <span>WhatsApp</span>
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-[#cbb26a] text-white hover:bg-[#cbb26a]/10 transition-all duration-300"
              >
                <a href={`tel:${contactInfo.phone}`} className="flex items-center space-x-2">
                  <Phone size={20} />
                  <span>Appelez-nous</span>
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Conditions;